<!-- Loading Spinner -->
<div *ngIf="isLoading" class="d-flex justify-content-center align-items-center" style="min-height: 400px;">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
</div>

<!-- Vehicle Edit Form -->
<div *ngIf="!isLoading" class="d-flex flex-column flex-column-fluid">
  <!-- Header -->
  <div class="card mb-5">
    <div class="card-header border-0 pt-6">
      <div class="card-title">
        <div class="d-flex align-items-center position-relative my-1">
          <h2 class="fw-bold m-0">Edit Vehicle</h2>
          <span class="badge badge-light-primary ms-3" *ngIf="vehicle">{{ vehicle.inventory_no }}</span>
        </div>
      </div>
      <div class="card-toolbar">
        <button type="button" class="btn btn-light btn-sm me-3" (click)="goBack()">
          <i class="ki-duotone ki-arrow-left fs-2">
            <span class="path1"></span>
            <span class="path2"></span>
          </i>
          Back to List
        </button>
      </div>
    </div>
  </div>

  <!-- Vehicle Images Section -->
  <div class="card mb-5">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h3 class="card-title">Vehicle Images</h3>
      <button type="button" class="btn btn-primary btn-sm" (click)="triggerMultiImageUpload()" [disabled]="uploadingImages">
        <i class="ki-duotone ki-plus fs-2">
          <span class="path1"></span>
          <span class="path2"></span>
        </i>
        Add Images
      </button>
    </div>
    <div class="card-body">
      <!-- Upload Progress -->
      <div *ngIf="uploadingImages" class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="text-muted">Uploading images...</span>
          <span class="badge badge-light-primary">{{ uploadProgress.current }} / {{ uploadProgress.total }}</span>
        </div>
        <div class="progress">
          <div class="progress-bar progress-bar-striped progress-bar-animated"
               role="progressbar"
               [style.width.%]="(uploadProgress.current / uploadProgress.total) * 100"
               [attr.aria-valuenow]="uploadProgress.current"
               [attr.aria-valuemax]="uploadProgress.total">
          </div>
        </div>
      </div>

      <!-- Existing Images -->
      <div class="row" *ngIf="vehicle?.pictures && vehicle.pictures.length > 0">
        <div class="col-md-2 mb-3" *ngFor="let picture of vehicle.pictures; let i = index">
          <div class="image-container position-relative">
            <img [src]="picture" [alt]="'Vehicle image ' + (i + 1)"
                 class="img-fluid rounded border cursor-pointer"
                 style="height: 120px; width: 100%; object-fit: cover;"
                 (click)="openImageModal(picture)">
            <div class="position-absolute bottom-0 start-0 end-0 bg-dark bg-opacity-75 text-white text-center py-1 rounded-bottom">
              <small>{{ i + 1 }}</small>
            </div>
            <!-- Remove button -->
            <button type="button"
                    class="btn btn-icon btn-sm btn-danger position-absolute top-0 end-0 m-1"
                    (click)="removeImage(i)"
                    style="width: 25px; height: 25px;">
              <i class="ki-duotone ki-cross fs-3">
                <span class="path1"></span>
                <span class="path2"></span>
              </i>
            </button>
          </div>
        </div>
      </div>

      <!-- Multi-image upload dropzone -->
      <div class="dropzone border-dashed border-2 border-gray-300 rounded p-6 text-center"
           *ngIf="!vehicle?.pictures || vehicle.pictures.length === 0"
           (click)="triggerMultiImageUpload()"
           style="cursor: pointer; min-height: 200px; display: flex; align-items: center; justify-content: center;">
        <div>
          <i class="ki-duotone ki-file-up text-primary fs-3x mb-3">
            <span class="path1"></span>
            <span class="path2"></span>
          </i>
          <h3 class="fs-5 fw-bold text-gray-900 mb-1">Drop files here or click to upload</h3>
          <span class="fs-7 fw-semibold text-gray-500">Upload multiple vehicle images</span>
        </div>
      </div>

      <!-- Hidden file input -->
      <input type="file"
             #multiImageInput
             multiple
             accept="image/*"
             (change)="onMultiImageSelected($event)"
             style="display: none;">
    </div>
  </div>

  <!-- Main Form -->
  <form [formGroup]="vehicleForm" (ngSubmit)="onSubmit()" novalidate>
    <div class="card">
      <div class="card-body p-9">

        <!-- Basic Information Section -->
        <div class="row mb-8">
          <div class="col-12">
            <h3 class="fw-bold text-dark mb-5">Basic Information</h3>
          </div>

          <!-- Row 1: Show On Home Page, Vehicle Type, Status -->
          <div class="col-md-4 mb-5">
            <label class="form-label">Show On Home Page</label>
            <div class="d-flex gap-3">
              <div class="form-check form-check-custom form-check-solid">
                <input class="form-check-input" type="radio" formControlName="show_on_home_page"
                       [value]="true" id="show_home_yes">
                <label class="form-check-label" for="show_home_yes">Yes</label>
              </div>
              <div class="form-check form-check-custom form-check-solid">
                <input class="form-check-input" type="radio" formControlName="show_on_home_page"
                       [value]="false" id="show_home_no">
                <label class="form-check-label" for="show_home_no">No</label>
              </div>
            </div>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Vehicle Type</label>
            <div class="d-flex gap-3">
              <div class="form-check form-check-custom form-check-solid">
                <input class="form-check-input" type="radio" formControlName="vehicle_type_radio"
                       value="new" id="vehicle_type_new">
                <label class="form-check-label" for="vehicle_type_new">New</label>
              </div>
              <div class="form-check form-check-custom form-check-solid">
                <input class="form-check-input" type="radio" formControlName="vehicle_type_radio"
                       value="used" id="vehicle_type_used">
                <label class="form-check-label" for="vehicle_type_used">Used</label>
              </div>
            </div>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Status</label>
            <div class="d-flex gap-2 flex-wrap">
              <div class="form-check form-check-custom form-check-solid">
                <input class="form-check-input" type="radio" formControlName="status_radio"
                       value="accident" id="status_accident">
                <label class="form-check-label" for="status_accident">Accident</label>
              </div>
              <div class="form-check form-check-custom form-check-solid">
                <input class="form-check-input" type="radio" formControlName="status_radio"
                       value="damaged" id="status_damaged">
                <label class="form-check-label" for="status_damaged">Damaged</label>
              </div>
              <div class="form-check form-check-custom form-check-solid">
                <input class="form-check-input" type="radio" formControlName="status_radio"
                       value="salvaged" id="status_salvaged">
                <label class="form-check-label" for="status_salvaged">Salvaged</label>
              </div>
              <div class="form-check form-check-custom form-check-solid">
                <input class="form-check-input" type="radio" formControlName="status_radio"
                       value="none" id="status_none">
                <label class="form-check-label" for="status_none">None</label>
              </div>
            </div>
          </div>

          <!-- Row 2: Stock Number, Status Dropdown, Stock Location -->
          <div class="col-md-4 mb-5">
            <label class="form-label required">Stock Number</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="inventory_no" placeholder="Enter stock number"
                   [class.is-invalid]="inventory_no?.invalid && inventory_no?.touched">
            <div *ngIf="inventory_no?.invalid && inventory_no?.touched" class="invalid-feedback">
              <div *ngIf="inventory_no?.errors?.['required']">Stock number is required</div>
            </div>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Status</label>
            <select class="form-select form-select-solid" formControlName="status_dropdown" [disabled]="true">
              <option value="now on sale">Now On Sale</option>
              <option value="sold">Sold</option>
            </select>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Stock Location</label>
            <select class="form-select form-select-solid"
                    formControlName="stock_location"
                    (change)="onStockLocationChange($any($event.target).value)">
              <option value="">Select Country</option>
              <option *ngFor="let country of countriesList" [value]="country.code">{{ country.label }}</option>
            </select>
          </div>

          <!-- Row 3: Stock Port, FOB Price, Special Offer -->
          <div class="col-md-4 mb-5">
            <label class="form-label">Stock Port</label>
            <select class="form-select form-select-solid"
                    formControlName="stock_port"
                    [disabled]="!stock_location?.value">
              <option value="">Select Port</option>
              <option *ngFor="let port of portsList" [value]="port.value">{{ port.label }}</option>
            </select>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">FOB Price</label>
            <div class="input-group">
              <input type="number" class="form-control form-control-solid"
                     formControlName="fob_price" placeholder="Enter FOB price">
              <span class="input-group-text">JPY</span>
            </div>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Special Offer</label>
            <div class="input-group">
              <input type="number" class="form-control form-control-solid"
                     formControlName="special_offer" placeholder="Enter special offer">
              <span class="input-group-text">JPY</span>
            </div>
          </div>

          <!-- Row 4: Auction Number, Lot Number, Auction Date -->
          <div class="col-md-4 mb-5">
            <label class="form-label">Auction Number</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="auction_number" placeholder="Enter auction number">
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Lot Number</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="lot_no" placeholder="Enter lot number">
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Auction Date</label>
            <input type="date" class="form-control form-control-solid"
                   formControlName="auction_date">
          </div>

          <!-- Row 5: POS No, Purchased From, Auction Price -->
          <div class="col-md-4 mb-5">
            <label class="form-label">POS No</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="pos_no" placeholder="Enter POS number">
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Purchased From</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="purchased_from" placeholder="Enter purchased from">
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Auction Price</label>
            <div class="input-group">
              <input type="number" class="form-control form-control-solid"
                     formControlName="auction_price" placeholder="Enter auction price">
              <span class="input-group-text">JPY</span>
            </div>
          </div>

          <!-- Row 6: Sales Person (single field) -->
          <div class="col-md-4 mb-5">
            <label class="form-label">Sales Person</label>
            <select class="form-select form-select-solid" formControlName="sales_person_id">
              <option value="">Select Sales Person</option>
              <option *ngFor="let person of salesPersonList" [value]="person.value">{{ person.label }}</option>
            </select>
          </div>
        </div>

        <!-- Vehicle Details Section -->
        <div class="row mb-8">
          <div class="col-12">
            <h3 class="fw-bold text-dark mb-5">Vehicle Details</h3>
          </div>

          <!-- Row 1: Vehicle Title (2 columns), Chassis No., Vehicle Type -->
          <div class="col-md-6 mb-5">
            <label class="form-label">Vehicle Title</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="vehicle_title" placeholder="Enter vehicle title">
          </div>

          <div class="col-md-3 mb-5">
            <label class="form-label">Chassis No.</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="vin_no" placeholder="Enter chassis number">
          </div>

          <div class="col-md-3 mb-5">
            <label class="form-label">Vehicle Type</label>
            <select class="form-select form-select-solid" formControlName="vehicle_type_dropdown">
              <option value="">Select Vehicle Type</option>
              <option *ngFor="let type of vehicleTypesList" [value]="type.value">{{ type.label }}</option>
            </select>
          </div>

          <!-- Row 2: Grade, Registration Month, Registration Year -->
          <div class="col-md-4 mb-5">
            <label class="form-label">Grade</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="grade" placeholder="Enter grade">
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Registration Month</label>
            <select class="form-select form-select-solid" formControlName="registration_month">
              <option value="">Select Month</option>
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Registration Year</label>
            <select class="form-select form-select-solid" formControlName="registration_year">
              <option value="">Select Year</option>
              <option *ngFor="let year of yearsList" [value]="year.value">{{ year.label }}</option>
            </select>
          </div>

          <!-- Row 3: Body Type, Mileage, Manufacture Month -->
          <div class="col-md-4 mb-5">
            <label class="form-label">Body Type</label>
            <select class="form-select form-select-solid" formControlName="body_type">
              <option value="">Select Body Type</option>
              <option *ngFor="let type of bodyTypesList" [value]="type.value">{{ type.label }}</option>
            </select>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label required">Mileage</label>
            <input type="number" class="form-control form-control-solid"
                   formControlName="mileage" placeholder="Enter mileage"
                   [class.is-invalid]="mileage?.invalid && mileage?.touched">
            <div *ngIf="mileage?.invalid && mileage?.touched" class="invalid-feedback">
              <div *ngIf="mileage?.errors?.['required']">Mileage is required</div>
              <div *ngIf="mileage?.errors?.['min']">Mileage must be 0 or greater</div>
            </div>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Manufacture Month</label>
            <select class="form-select form-select-solid" formControlName="manufacture_month">
              <option value="">Select Month</option>
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
          </div>

          <!-- Row 4: Manufacture Year, Make, Engine Capacity -->
          <div class="col-md-4 mb-5">
            <label class="form-label">Manufacture Year</label>
            <select class="form-select form-select-solid" formControlName="manufacture_year">
              <option value="">Select Year</option>
              <option *ngFor="let year of yearsList" [value]="year.value">{{ year.label }}</option>
            </select>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Make</label>
            <select class="form-select form-select-solid"
                    formControlName="company_slug"
                    (change)="onMakeChange($any($event.target).value)">
              <option value="">Select Make</option>
              <option *ngFor="let make of makesList" [value]="make.value">{{ make.label }}</option>
            </select>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Engine Capacity</label>
            <div class="input-group">
              <input type="number" class="form-control form-control-solid"
                     formControlName="engine_capacity" placeholder="Enter engine capacity" step="0.1">
              <span class="input-group-text">CC</span>
            </div>
          </div>

          <!-- Row 5: Interior Grade, Model, Seating Capacity -->
          <div class="col-md-4 mb-5">
            <label class="form-label">Interior Grade</label>
            <select class="form-select form-select-solid" formControlName="interior_grade">
              <option value="">Select Interior Grade</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
              <option value="D">D</option>
            </select>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Model</label>
            <select class="form-select form-select-solid" formControlName="slug"
                    (change)="onModelChange($any($event.target).value)">
              <option value="">Select Model</option>
              <option *ngFor="let model of modelsList" [value]="model.value">{{ model.label }}</option>
            </select>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Seating Capacity</label>
            <input type="number" class="form-control form-control-solid"
                   formControlName="seating_capacity" placeholder="Enter seating capacity">
          </div>

          <!-- Row 6: Exterior Grade, Model Code, M3 -->
          <div class="col-md-4 mb-5">
            <label class="form-label">Exterior Grade</label>
            <select class="form-select form-select-solid" formControlName="exterior_grade">
              <option value="">Select Exterior Grade</option>
              <option value="1">1</option>
              <option value="1.5">1.5</option>
              <option value="2">2</option>
              <option value="2.5">2.5</option>
              <option value="3">3</option>
              <option value="3.5">3.5</option>
              <option value="4">4</option>
              <option value="4.5">4.5</option>
              <option value="5">5</option>
            </select>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Model Code</label>
            <select class="form-select form-select-solid" formControlName="model_code">
              <option value="">Select Model Code</option>
              <option *ngFor="let code of modelCodesList" [value]="code.value">{{ code.label }}</option>
            </select>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">M3</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="m3" placeholder="Enter M3">
          </div>

          <!-- Row 7: 2WD/4WD, Width, Height -->
          <div class="col-md-4 mb-5">
            <label class="form-label">2WD/4WD</label>
            <select class="form-select form-select-solid" formControlName="drive_2wd_4wd">
              <option value="">Select Drive Type</option>
              <option value="2WD">2WD</option>
              <option value="4WD">4WD</option>
            </select>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Width</label>
            <input type="number" class="form-control form-control-solid"
                   formControlName="car_width" placeholder="Enter width" step="0.1">
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Height</label>
            <input type="number" class="form-control form-control-solid"
                   formControlName="car_height" placeholder="Enter height" step="0.1">
          </div>

          <!-- Row 8: Length, Transmission, Loading Capacity -->
          <div class="col-md-4 mb-5">
            <label class="form-label">Length</label>
            <input type="number" class="form-control form-control-solid"
                   formControlName="car_length" placeholder="Enter length" step="0.1">
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label required">Transmission</label>
            <select class="form-select form-select-solid"
                    formControlName="transmission"
                    (change)="onTransmissionChange($any($event.target).value)"
                    [class.is-invalid]="transmission?.invalid && transmission?.touched">
              <option value="">Select Transmission</option>
              <option *ngFor="let trans of transmissionList" [value]="trans.value">{{ trans.label }}</option>
            </select>
            <div *ngIf="transmission?.invalid && transmission?.touched" class="invalid-feedback">
              <div *ngIf="transmission?.errors?.['required']">Transmission is required</div>
            </div>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Loading Capacity</label>
            <input type="number" class="form-control form-control-solid"
                   formControlName="loading_capacity" placeholder="Enter loading capacity" step="0.1">
          </div>

          <!-- Row 9: Video Url, Fuel, Weight -->
          <div class="col-md-4 mb-5">
            <label class="form-label">Video Url</label>
            <input type="url" class="form-control form-control-solid"
                   formControlName="video_url" placeholder="Enter video URL">
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label required">Fuel</label>
            <select class="form-select form-select-solid"
                    formControlName="fuel_type_slug"
                    (change)="onFuelTypeChange($any($event.target).value)"
                    [class.is-invalid]="fuel_type_slug?.invalid && fuel_type_slug?.touched">
              <option value="">Select Fuel Type</option>
              <option *ngFor="let fuel of fuelTypesList" [value]="fuel.value">{{ fuel.label }}</option>
            </select>
            <div *ngIf="fuel_type_slug?.invalid && fuel_type_slug?.touched" class="invalid-feedback">
              <div *ngIf="fuel_type_slug?.errors?.['required']">Fuel type is required</div>
            </div>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Weight</label>
            <div class="input-group">
              <input type="number" class="form-control form-control-solid"
                     formControlName="weight" placeholder="Enter weight" step="0.1">
              <span class="input-group-text">kg</span>
            </div>
          </div>

          <!-- Row 10: Hours(Machinery), Exterior Color, Doors -->
          <div class="col-md-4 mb-5">
            <label class="form-label">Hours(Machinery)</label>
            <input type="number" class="form-control form-control-solid"
                   formControlName="hours_machinery" placeholder="Enter hours">
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Exterior Color</label>
            <select class="form-select form-select-solid" formControlName="exterior_color">
              <option value="">Select Exterior Color</option>
              <option *ngFor="let color of colorsList" [value]="color.label">{{ color.label }}</option>
            </select>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Doors</label>
            <select class="form-select form-select-solid" formControlName="doors">
              <option value="">Select Doors</option>
              <option value="2">2 Doors</option>
              <option value="3">3 Doors</option>
              <option value="4">4 Doors</option>
              <option value="5">5 Doors</option>
            </select>
          </div>

          <!-- Row 11: Drive, HS-Code, Engine Number -->
          <div class="col-md-4 mb-5">
            <label class="form-label">Drive</label>
            <select class="form-select form-select-solid" formControlName="drive_type">
              <option value="">Select Drive Type</option>
              <option value="2wd">2WD</option>
              <option value="4wd">4WD</option>
              <option value="awd">AWD</option>
            </select>
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">HS-Code</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="hs_code" placeholder="Enter HS-Code">
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Engine Number</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="engine_number" placeholder="Enter engine number">
          </div>

          <!-- Row 12: Interior Color, Steering, Year -->
          <div class="col-md-4 mb-5">
            <label class="form-label">Interior Color</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="interior_color" placeholder="Enter interior color">
          </div>

          <div class="col-md-4 mb-5">
            <label class="form-label">Steering</label>
            <select class="form-select form-select-solid" formControlName="steering">
              <option value="">Select Steering</option>
              <option value="left">Left Hand Drive</option>
              <option value="right">Right Hand Drive</option>
            </select>
          </div>



          <!-- COMMENTED OUT - Old fields not in new specification -->
          <!--
          <div class="col-md-3 mb-5">
            <label class="form-label">Registration Number</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="registration_number" placeholder="Enter registration number">
          </div>
          -->

          <!-- COMMENTED OUT - Old fields not in new specification -->
          <!--
          <div class="col-md-3 mb-5">
            <label class="form-label">Auction House</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="auction_house" placeholder="Enter auction house">
          </div>

          <div class="col-md-3 mb-5">
            <label class="form-label required">Price</label>
            <input type="number" class="form-control form-control-solid"
                   formControlName="price" placeholder="Enter price"
                   [class.is-invalid]="price?.invalid && price?.touched">
            <div *ngIf="price?.invalid && price?.touched" class="invalid-feedback">
              <div *ngIf="price?.errors?.['required']">Price is required</div>
              <div *ngIf="price?.errors?.['min']">Price must be 0 or greater</div>
            </div>
          </div>

          <div class="col-md-3 mb-5">
            <label class="form-label">Location</label>
            <input type="text" class="form-control form-control-solid"
                   formControlName="location" placeholder="Enter location">
          </div>

          <div class="col-md-6 mb-5">
            <label class="form-label">Inspection Expiry Date</label>
            <input type="date" class="form-control form-control-solid"
                   formControlName="inspection_expiry_date">
          </div>

          <div class="col-md-6 mb-5">
            <label class="form-label">Other Specifications</label>
            <textarea class="form-control form-control-solid"
                      formControlName="other_specifications"
                      rows="3" placeholder="Enter other specifications"></textarea>
          </div>

          <div class="col-md-6 mb-5">
            <label class="form-label">Remarks</label>
            <textarea class="form-control form-control-solid"
                      formControlName="remarks"
                      rows="3" placeholder="Enter remarks"></textarea>
          </div>
          -->



          <!-- Description -->
          <div class="col-12 mb-5">
            <label class="form-label">Description</label>
            <textarea class="form-control form-control-solid"
                      formControlName="one_line_description"
                      rows="3" placeholder="Enter vehicle description"></textarea>
          </div>
        </div>

        <!-- Features Section -->
        <div class="row mb-8">
          <div class="col-12">
            <h3 class="fw-bold text-dark mb-5">Features & Options</h3>
          </div>

          <!-- Good Points -->
          <div class="col-md-3 mb-5">
            <div class="card h-100">
              <div class="card-header bg-light-primary">
                <h4 class="card-title text-primary mb-0">Good Points</h4>
              </div>
              <div class="card-body">
                <div class="form-check form-check-custom form-check-solid mb-2" *ngFor="let accessory of goodPointsAccessories">
                  <input class="form-check-input" type="checkbox" [formControlName]="accessory.slug" [id]="accessory.slug">
                  <label class="form-check-label" [for]="accessory.slug">{{accessory.name}}</label>
                </div>
              </div>
            </div>
          </div>

          <!-- Mechanical Faults -->
          <div class="col-md-3 mb-5">
            <div class="card h-100">
              <div class="card-header bg-light-warning">
                <h4 class="card-title text-warning mb-0">Mechanical Faults</h4>
              </div>
              <div class="card-body">
                <div class="form-check form-check-custom form-check-solid mb-2" *ngFor="let accessory of mechanicalFaultsAccessories">
                  <input class="form-check-input" type="checkbox" [formControlName]="accessory.slug" [id]="accessory.slug">
                  <label class="form-check-label" [for]="accessory.slug">{{accessory.name}}</label>
                </div>
              </div>
            </div>
          </div>

          <!-- Other Options -->
          <div class="col-md-3 mb-5">
            <div class="card h-100">
              <div class="card-header bg-light-info">
                <h4 class="card-title text-info mb-0">Other Options</h4>
              </div>
              <div class="card-body">
                <label class="form-label">Additional Information</label>
                <textarea class="form-control form-control-solid"
                          formControlName="additional_information"
                          rows="15" placeholder="Enter additional information..."></textarea>
              </div>
            </div>
          </div>

          <!-- Remarks -->
          <div class="col-md-3 mb-5">
            <div class="card h-100">
              <div class="card-header bg-light-dark">
                <h4 class="card-title text-dark mb-0">Remarks</h4>
              </div>
              <div class="card-body">
                <textarea class="form-control form-control-solid"
                          formControlName="features_remarks"
                          rows="15" placeholder="Enter additional remarks about features..."></textarea>
              </div>
            </div>
          </div>


        </div>

        <!-- Additional Features Section -->
        <div class="row mb-8">
          <div class="col-12">
            <h3 class="fw-bold text-dark mb-5">Additional Features</h3>
          </div>

          <!-- Accessories & Options -->
          <div class="col-12 mb-5">
            <div class="card">
              <div class="card-header bg-light-success">
                <h4 class="card-title text-success mb-0">Accessories & Options</h4>
              </div>
              <div class="card-body">
                <!-- Loop through each group -->
                <div *ngFor="let groupName of accessoryGroupNames" class="mb-4">
                  <!-- Group Header -->
                  <div class="mb-3">
                    <h5 class="text-success fw-bold border-bottom border-success pb-2">{{ groupName }}</h5>
                  </div>

                  <!-- Accessories in this group -->
                  <div class="row">
                    <div class="col-md-3" *ngFor="let accessory of groupedAccessoriesAndOptions[groupName]">
                      <div class="form-check form-check-custom form-check-solid mb-2">
                        <input class="form-check-input" type="checkbox" [formControlName]="accessory.slug" [id]="accessory.slug">
                        <label class="form-check-label" [for]="accessory.slug">{{accessory.name}}</label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Show message if no accessories -->
                <div *ngIf="accessoryGroupNames.length === 0" class="text-muted text-center py-4">
                  No accessories available
                </div>
              </div>
            </div>
          </div>


        </div>





        <!-- Form Actions -->
        <div class="row">
          <div class="col-12">
            <div class="d-flex justify-content-end">
              <button type="button" class="btn btn-light me-3" (click)="goBack()" [disabled]="isSaving">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary" [disabled]="isSaving">
                <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                <span *ngIf="isSaving">Updating...</span>
                <span *ngIf="!isSaving">Update Vehicle</span>
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  </form>
</div>
